import React, { useState, useEffect } from 'react';
import { memberApprovalService } from '..';
import { toast } from 'react-hot-toast';
import { UserCheck, UserX, Search, User, Users } from 'lucide-react';
import { BRAND_COLORS, NEUTRAL_COLORS, SYSTEM_COLORS } from '../../../constants/brandPalette';
import ConfirmDeleteModal from '../../../components/common/ConfirmDeleteModal';

const fmtDate = (ts) => {
  if (!ts) return 'N/A';
  try { return new Date(ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return 'N/A'; }
};

const RoleBadge = ({ role }) => (
  <span
    className="inline-flex rounded-full px-2.5 py-1 text-xs font-semibold capitalize"
    style={{ backgroundColor: SYSTEM_COLORS.primaryTint, color: BRAND_COLORS.primary }}
  >
    {(role || 'N/A').replace(/_/g, ' ')}
  </span>
);

const MemberApprovals = () => {
  const [pendingMembers, setPendingMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [rejectTarget, setRejectTarget] = useState(null);

  useEffect(() => { fetchPendingMembers(); }, []);

  const fetchPendingMembers = async () => {
    try {
      setLoading(true);
      const members = await memberApprovalService.getPendingApprovals();
      setPendingMembers(Array.isArray(members) ? members : []);
    } catch {
      toast.error('Failed to load pending approvals');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (member) => {
    try {
      await memberApprovalService.approveMember(member.id);
      toast.success(`${member.name} approved`);
      fetchPendingMembers();
    } catch {
      toast.error('Failed to approve member');
    }
  };

  const confirmReject = async () => {
    try {
      await memberApprovalService.rejectMember(rejectTarget.id);
      toast.success(`${rejectTarget.name} rejected`);
      setRejectTarget(null);
      fetchPendingMembers();
    } catch {
      toast.error('Failed to reject member');
    }
  };

  const filtered = pendingMembers.filter(m =>
    m.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.role?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen px-4 py-6 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto space-y-6">

        {/* Header */}
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: BRAND_COLORS.primary }}>
            Onboarding
          </p>
          <h1 className="mt-1 text-2xl font-bold sm:text-3xl" style={{ color: NEUTRAL_COLORS.black }}>
            Member Approvals
          </h1>
          <p className="mt-1 text-sm" style={{ color: SYSTEM_COLORS.textMuted }}>
            Review and approve pending member registrations
          </p>
        </div>

        {/* Search */}
        <div
          className="flex items-center gap-2 rounded-xl border px-3 py-2.5"
          style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurface }}
        >
          <Search size={15} className="flex-shrink-0" style={{ color: SYSTEM_COLORS.textSoft }} />
          <input
            type="text"
            placeholder="Search by name, email, or role…"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="flex-1 text-sm bg-transparent outline-none"
            style={{ color: NEUTRAL_COLORS.black }}
          />
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div
              className="w-8 h-8 border-b-2 rounded-full animate-spin"
              style={{ borderColor: BRAND_COLORS.primary }}
            />
          </div>
        ) : filtered.length === 0 ? (
          <div
            className="flex flex-col items-center justify-center gap-3 py-16 rounded-xl border"
            style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurface }}
          >
            <div
              className="flex items-center justify-center w-12 h-12 rounded-lg"
              style={{ backgroundColor: SYSTEM_COLORS.shellSurfaceMuted }}
            >
              <Users size={22} style={{ color: BRAND_COLORS.primary }} />
            </div>
            <p className="text-sm font-semibold" style={{ color: NEUTRAL_COLORS.black }}>
              {searchTerm ? 'No members match your search' : 'No Pending Approvals'}
            </p>
            <p className="text-xs" style={{ color: SYSTEM_COLORS.textMuted }}>
              {searchTerm ? 'Try a different query' : 'All member registrations have been processed'}
            </p>
          </div>
        ) : (
          <div
            className="rounded-xl border overflow-hidden"
            style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft }}
          >
            {/* Desktop table */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="min-w-full">
                <thead style={{ backgroundColor: SYSTEM_COLORS.surfaceSubtle }}>
                  <tr>
                    {['Member', 'Email', 'Role', 'Requested', 'Actions'].map((h, i) => (
                      <th
                        key={h}
                        className={`px-4 py-3 text-xs font-semibold uppercase tracking-wider ${i === 4 ? 'text-right' : 'text-left'}`}
                        style={{ color: SYSTEM_COLORS.textMuted }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y" style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
                  {filtered.map(member => (
                    <tr key={member.id} style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <div
                            className="flex items-center justify-center w-9 h-9 rounded-md flex-shrink-0"
                            style={{ backgroundColor: SYSTEM_COLORS.primaryTint }}
                          >
                            <User size={15} style={{ color: BRAND_COLORS.primary }} />
                          </div>
                          <span className="text-sm font-semibold" style={{ color: NEUTRAL_COLORS.black }}>
                            {member.name}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-sm" style={{ color: SYSTEM_COLORS.textMuted }}>{member.email}</span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <RoleBadge role={member.role} />
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-sm" style={{ color: SYSTEM_COLORS.textMuted }}>{fmtDate(member.created_at)}</span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleApprove(member)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold hover:opacity-90 transition-opacity"
                            style={{ backgroundColor: SYSTEM_COLORS.successTint, color: SYSTEM_COLORS.success }}
                          >
                            <UserCheck size={13} />
                            Approve
                          </button>
                          <button
                            onClick={() => setRejectTarget(member)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold hover:opacity-90 transition-opacity"
                            style={{ backgroundColor: SYSTEM_COLORS.dangerTint, color: SYSTEM_COLORS.danger }}
                          >
                            <UserX size={13} />
                            Reject
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="sm:hidden divide-y" style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
              {filtered.map(member => (
                <div key={member.id} className="px-4 py-4 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div
                        className="flex items-center justify-center w-9 h-9 rounded-md flex-shrink-0"
                        style={{ backgroundColor: SYSTEM_COLORS.primaryTint }}
                      >
                        <User size={15} style={{ color: BRAND_COLORS.primary }} />
                      </div>
                      <div>
                        <p className="text-sm font-semibold" style={{ color: NEUTRAL_COLORS.black }}>{member.name}</p>
                        <p className="text-xs" style={{ color: SYSTEM_COLORS.textMuted }}>{member.email}</p>
                      </div>
                    </div>
                    <RoleBadge role={member.role} />
                  </div>
                  <p className="text-xs" style={{ color: SYSTEM_COLORS.textSoft }}>Requested {fmtDate(member.created_at)}</p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleApprove(member)}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-semibold hover:opacity-90"
                      style={{ backgroundColor: SYSTEM_COLORS.successTint, color: SYSTEM_COLORS.success }}
                    >
                      <UserCheck size={13} /> Approve
                    </button>
                    <button
                      onClick={() => setRejectTarget(member)}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-semibold hover:opacity-90"
                      style={{ backgroundColor: SYSTEM_COLORS.dangerTint, color: SYSTEM_COLORS.danger }}
                    >
                      <UserX size={13} /> Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {!loading && pendingMembers.length > 0 && (
          <p className="text-xs" style={{ color: SYSTEM_COLORS.textMuted }}>
            Showing {filtered.length} of {pendingMembers.length} pending approval{pendingMembers.length !== 1 ? 's' : ''}
          </p>
        )}
      </div>

      <ConfirmDeleteModal
        isOpen={!!rejectTarget}
        onClose={() => setRejectTarget(null)}
        onConfirm={confirmReject}
        title="Reject Member"
        description={rejectTarget ? `Reject "${rejectTarget.name}"? They will need to register again.` : ''}
      />
    </div>
  );
};

export default MemberApprovals;
